import { useIsConsumerSession } from '@/hooks/use-app-mode';
import { hasReachedFlowCompletion, resolveConsumerEntryStep } from '../lib/flow-completion';
import { consumerStepPath } from '../lib/paths';
import { currentConsumerStep } from '../lib/steps';
import { useConsumerPlan } from './use-consumer-plan';

function visitedKey(clientId: string): string {
  return `consumer-flow-visited:${clientId}`;
}

/** Records that this browser session has been in the guided flow, so re-entry stops redirecting. */
export function markConsumerFlowVisited(clientId: string) {
  try {
    sessionStorage.setItem(visitedKey(clientId), 'true');
  } catch {
    // Storage can be unavailable (private browsing); resuming then simply happens again.
  }
}

/** Opening the client from the profile list should resume the guided flow again, so forget the visit. */
export function forgetConsumerFlowVisit(clientId: string) {
  try {
    sessionStorage.removeItem(visitedKey(clientId));
  } catch {
    // Without storage nothing was remembered.
  }
}

function wasConsumerFlowVisited(clientId: string): boolean {
  try {
    return sessionStorage.getItem(visitedKey(clientId)) === 'true';
  } catch {
    return false;
  }
}

/**
 * Where a returning d2c consumer should land instead of the first results slide: Your Plan once they
 * have finished, otherwise the first consumer step. Only on their first arrival in a browser
 * session, so going back through the results on purpose isn't bounced forward again.
 */
export function useConsumerResumePath(clientId: string) {
  const shouldCheck = useIsConsumerSession() && !wasConsumerFlowVisited(clientId);
  const { plan, applications, isLoading } = useConsumerPlan(clientId, { enabled: shouldCheck });
  const hasPlace =
    hasReachedFlowCompletion(plan, applications) || !!currentConsumerStep(plan?.lastStep);

  return {
    isChecking: shouldCheck && isLoading,
    resumePath:
      shouldCheck && hasPlace
        ? consumerStepPath(clientId, resolveConsumerEntryStep(plan, applications))
        : null,
  };
}
